import { z } from "zod";
import { InvoiceStatus } from "@common/types/invoice";
import { accountOverridesRequestSchema } from "../accounting/account-override.schema";

const patchImporterInvoiceLineItemSchema = z.object({
    id: z.string().uuid().optional(),
    productOrService: z.string().max(255).nullable().optional(),
    catalogItemId: z.string().uuid().nullable().optional(),
    catalogItemPriceId: z.string().uuid().nullable().optional(),
    description: z.string().max(1000).nullable(),
    amount: z.number().finite(),
    quantityDecimal: z.number().finite().nullable().optional(),
    unitAmount: z.number().finite().nullable().optional(),
    accountOverrides: accountOverridesRequestSchema.optional(),
    accountsReceivableOverrides: accountOverridesRequestSchema.optional(),
    // Transitional legacy fields. New callers should use the
    // per-ledger override arrays; these resolve to the entity's
    // primary accrual ledger server-side.
    accountId: z.string().uuid().nullable().optional(),
    accountsReceivableId: z.string().uuid().nullable().optional(),
    classIds: z.array(z.string().uuid()).max(20).optional(),
});

export type PatchImporterInvoiceLineItemBody = z.infer<
    typeof patchImporterInvoiceLineItemSchema
>;

export const patchImporterInvoiceItemSchema = z.object({
    id: z
        .string()
        .uuid()
        .optional()
        .describe(
            "Existing MCP invoice id to edit (line ids are preserved). Omit to create a new invoice.",
        ),
    entityId: z.coerce.number().int(),
    financialAccountId: z.coerce.number().int().optional(),
    invoiceNumber: z.string().max(255).nullable(),
    counterpartyId: z.string().uuid().nullable().optional(),
    description: z.string().max(1000).nullable().optional(),
    due: z.coerce.date().nullable().optional(),
    createdAt: z.coerce.date().optional(),
    status: z.nativeEnum(InvoiceStatus),
    memo: z.string().max(5000).nullable().optional(),
    currency: z
        .string()
        .regex(/^[a-zA-Z]{3}$/)
        .transform((v) => v.toLowerCase())
        .default("usd"),
    lines: z.array(patchImporterInvoiceLineItemSchema).min(1).max(100),
    tax: z.number().finite().nonnegative().optional(),
    discount: z.number().finite().nonnegative().optional(),
    deposit: z.number().finite().nonnegative().optional(),
});

export type PatchImporterInvoiceItem = z.infer<
    typeof patchImporterInvoiceItemSchema
>;

// Behavior-preserving base object + refinement predicates exposed for
// agent-layer (MCP) reuse. The MCP invoices_update override requires an upsert
// id while keeping every guard below intact. Do not change behavior here — the
// importer API relies on patchImporterInvoicesBodySchema being identical.
export const patchImporterInvoicesBodyObject = z.object({
    idsToRemove: z
        .array(z.string().uuid())
        .max(50)
        .default([])
        .refine((ids) => new Set(ids).size === ids.length, {
            message: "idsToRemove must not contain duplicate IDs",
        }),
    upserts: z.array(patchImporterInvoiceItemSchema).max(50).default([]),
});

export const patchImporterInvoicesBodyRefinements: ReadonlyArray<{
    check: (body: {
        idsToRemove: string[];
        upserts: PatchImporterInvoiceItem[];
    }) => boolean;
    message: string;
}> = [
    {
        check: ({ idsToRemove, upserts }) =>
            idsToRemove.length > 0 || upserts.length > 0,
        message: "At least one of idsToRemove or upserts must be non-empty",
    },
    {
        check: ({ upserts }) => {
            const upsertIds = upserts
                .map((u) => u.id)
                .filter((id): id is string => id !== undefined);
            return new Set(upsertIds).size === upsertIds.length;
        },
        message: "upserts must not contain duplicate IDs",
    },
    {
        check: ({ idsToRemove, upserts }) => {
            const upsertIds = new Set(
                upserts
                    .map((u) => u.id)
                    .filter((id): id is string => id !== undefined),
            );
            return idsToRemove.every((id) => !upsertIds.has(id));
        },
        message: "An ID may not appear in both idsToRemove and upserts",
    },
];

// Chained explicitly (not reduced) so the inferred output type is preserved;
// each .refine reuses the shared predicate above so logic never drifts.
export const patchImporterInvoicesBodySchema = patchImporterInvoicesBodyObject
    .refine(patchImporterInvoicesBodyRefinements[0].check, {
        message: patchImporterInvoicesBodyRefinements[0].message,
    })
    .refine(patchImporterInvoicesBodyRefinements[1].check, {
        message: patchImporterInvoicesBodyRefinements[1].message,
    })
    .refine(patchImporterInvoicesBodyRefinements[2].check, {
        message: patchImporterInvoicesBodyRefinements[2].message,
    });

export type PatchImporterInvoicesBody = z.infer<
    typeof patchImporterInvoicesBodySchema
>;
